import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { isSteam } from '@/lib/platform'
import {
  forgetPasswordUrlReturningTo,
  safeReturnTarget,
  signUpUrlReturningTo,
} from '@/lib/return-target'

export function SignInPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const { isLoading, signIn } = useAuth()
  const { t } = useI18n()
  const [searchParams] = useSearchParams()

  if (isSteam) {
    return (
      <div className="h-screen pt-16">
        <div className="mx-auto w-full max-w-sm pt-24 md:max-w-md">
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12">
              <Loader2 size={32} className="animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Connecting via Steam...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen pt-16">
      <div className="mx-auto w-full max-w-sm pt-24 md:max-w-md">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg md:text-xl">
              {t('auth.signIn.title')}
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              {t('auth.signIn.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">{t('auth.signIn.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.signIn.emailPlaceholder')}
                  required
                  onChange={(e) => {
                    setEmail(e.target.value)
                  }}
                  value={email}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">{t('auth.signIn.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('auth.signIn.passwordPlaceholder')}
                  autoComplete="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
                onClick={() => signIn({ email, password })}
              >
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  t('auth.signIn.button')
                )}
              </Button>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col justify-between gap-2 md:flex-row">
            <div className="text-sm text-muted-foreground">
              {t('auth.signIn.noAccount')}{' '}
              <Link
                to={signUpUrlReturningTo(
                  safeReturnTarget(searchParams.get('redirect'), '/dashboard'),
                )}
                className="pl-1 text-primary hover:underline"
              >
                {t('auth.signIn.signUpNow')}
              </Link>
            </div>
            <div className="text-sm text-muted-foreground">
              <Link
                to={forgetPasswordUrlReturningTo(
                  safeReturnTarget(searchParams.get('redirect'), '/dashboard'),
                )}
                className="pl-1 hover:underline"
              >
                {t('auth.signIn.forgetPassword')}
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
